import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';
import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  HttpLink,
  ApolloProvider as ApolloClientProvider,
  NormalizedCacheObject,
  split,
} from '@apollo/client';
import { fromPromise } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient } from 'graphql-ws';
import { useAuth } from '../auth/AuthProvider';
import SentryService from '../sentry/Sentry.service';
import { FullScreenLoader } from '@/shared/components/FullScreenLoader';
import { LOG_WS_EVENTS } from '@/shared/constants/flags';
import { EnvironmentConfig } from '@/shared/config';
import useConfigStore from '@/stores/useConfigStore';
import useStore from '@/stores/useStore';

const WEBSOCKET_PING_INTERVAL = 25000;

interface ApolloAuthContextType {
  client: ApolloClient<NormalizedCacheObject>;
  logOut: () => Promise<void>;
}

interface CustomApolloProviderProps {
  children: ReactNode;
}

const ApolloAuthContext = createContext<ApolloAuthContextType | null>(null);

/*
 * Wrapper for the default Apollo Provider to allow dynamic fetching of
 * access token as needed and setting it on every request
 */
export const ApolloProvider: React.FC<CustomApolloProviderProps> = ({
  children,
}) => {
  const { loadingAuth, getAccessToken, auth0Logout } = useAuth();
  const environment = useConfigStore((state) => state.environment);

  const [client, setClient] =
    useState<ApolloClient<NormalizedCacheObject> | null>(null);
  const pingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const createApolloClient = (
    environment: EnvironmentConfig,
    getAccessToken: () => Promise<string | null>,
  ) => {
    const graphqlApiUrl = environment.server.baseUrl + '/api/graphql';

    const httpLink = new HttpLink({
      uri: graphqlApiUrl,
    });

    const authLink = new ApolloLink((operation, forward) => {
      return fromPromise(
        getAccessToken()
          .then((token) => {
            operation.setContext(({ headers = {} }) => ({
              headers: {
                ...headers,
                authorization: token ? `Bearer ${token}` : '',
              },
            }));
            return forward(operation);
          })
          .catch((error) => {
            SentryService.captureError('Error fetching access token', error);
            return forward(operation);
          }),
      ).flatMap(() => forward(operation));
    });

    const wsUrl = graphqlApiUrl
      ? graphqlApiUrl.replace(/^http(s?):/, !__DEV__ ? 'wss:' : 'ws:')
      : '';

    const wsLink = new GraphQLWsLink(
      createClient({
        url: wsUrl,
        connectionParams: async () => {
          const token = await getAccessToken();
          return {
            authorization: token ? `Bearer ${token}` : '',
          };
        },
        lazy: true,
        retryAttempts: 10,
        shouldRetry: () => true,
        on: {
          connected: () => LOG_WS_EVENTS && console.log('WebSocket Connected'),
          opened: (socket) => {
            LOG_WS_EVENTS && console.log('WebSocket Opened', socket);

            const ws = socket as WebSocket;
            // Keep WebSocket alive (Prevent idle disconnection)
            pingIntervalRef.current = setInterval(() => {
              if (ws.readyState === WebSocket.OPEN) {
                LOG_WS_EVENTS && console.log('Pinging websocket server...');
                ws.send(JSON.stringify({ type: 'ping' }));
              }
            }, WEBSOCKET_PING_INTERVAL);

            ws.addEventListener('close', () => {
              LOG_WS_EVENTS &&
                console.log('WebSocket closed. Clearing ping interval.');
              if (pingIntervalRef.current) {
                clearInterval(pingIntervalRef.current);
                pingIntervalRef.current = null;
              }
            });
          },
          closed: () => LOG_WS_EVENTS && console.log('WebSocket Disconnected'),
          error: (error) => {
            SentryService.captureError('WebSocket Error', error);
          },
          message: (msg) =>
            LOG_WS_EVENTS && console.log('WebSocket Message:', msg),
        },
      }),
    );

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);
        return (
          definition.kind === 'OperationDefinition' &&
          definition.operation === 'subscription'
        );
      },
      wsLink,
      authLink.concat(httpLink),
    );

    return new ApolloClient({
      link: splitLink,
      cache: new InMemoryCache(),
    });
  };

  const logOut = async () => {
    // Clear ping interval before clearing store
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    await client?.clearStore();
    setClient(null);

    await auth0Logout();
    useStore.getState().resetStore();

    const newClient = createApolloClient(environment, getAccessToken);
    setClient(newClient);
  };

  useEffect(() => {
    if (loadingAuth) {
      return;
    }

    __DEV__ && console.log(`Initializing Apollo Client: ${environment.name}`);

    // Clear any existing ping interval before creating new client
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
      pingIntervalRef.current = null;
    }

    const newClient = createApolloClient(environment, getAccessToken);
    setClient(newClient);

    // Cleanup function for when component unmounts or dependencies change
    return () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = null;
      }
    };
  }, [getAccessToken, loadingAuth, environment]);

  if (!client) {
    return <FullScreenLoader />;
  }

  return (
    <ApolloAuthContext.Provider value={{ client, logOut }}>
      <ApolloClientProvider client={client}>{children}</ApolloClientProvider>
    </ApolloAuthContext.Provider>
  );
};

export const useApollo = () => {
  const context = useContext(ApolloAuthContext);
  if (!context) {
    throw new Error('useApollo must be used within an ApolloProvider');
  }
  return context;
};
